import {
  Component,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
  OnInit,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, Location }      from '@angular/common';
import { FormsModule }       from '@angular/forms';
import { Router, ActivatedRoute }            from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

import {
  BlogService,
  IBlockInput,
  BlogStatus,
} from '../../shared/services/blog.service';
import { UserService }    from '../../shared/services/user.service';
import { HashtagService, HashtagSuggestion } from '../../shared/services/hashtag.service';
import { HashtagInputDirective }    from '../../shared/directives/hashtag-input.directive';
import { HashtagDropdownComponent } from '../../components/ui/dropdown/dropdown-item/hashtag-dropdown';
import { EmojiPickerComponent } from '../../components/ui/emoji-picker/emoji-picker';
import { LinkPreviewComponent } from '../../components/ui/link-preview/link-preview';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { isHeicFile, readAsPreviewDataUrl } from '../../shared/utils/heic.util';
import { ToastrService } from 'ngx-toastr';

export interface BlogUser {
  _id: string;
  displayName: string;
  userName: string;
  profileImage: { urlSmall?: string } | null;
  isFriend: boolean;
}

@Component({
  selector:    'app-create-blog',
  standalone:  true,
  imports:     [CommonModule, FormsModule, HashtagInputDirective, HashtagDropdownComponent, EmojiPickerComponent, LinkPreviewComponent],
  templateUrl: './create-blog.html',
})
export class CreateBlog implements OnInit {

  // ── Editor DOM refs ───────────────────────────────────────────────────────
  @ViewChild('editorBody')    editorBody!:    ElementRef<HTMLDivElement>;
  @ViewChild('colorPicker')   colorPicker!:   ElementRef<HTMLInputElement>;
  @ViewChild('bgColorPicker') bgColorPicker!: ElementRef<HTMLInputElement>;
  @ViewChild('imageInput')    imageInput!:    ElementRef<HTMLInputElement>;
  @ViewChild('photoInput')    photoInput!:    ElementRef<HTMLInputElement>;
  @ViewChild('videoInput')    videoInput!:    ElementRef<HTMLInputElement>;

  // ── Blog fields ───────────────────────────────────────────────────────────
  readonly MAX_TITLE_LENGTH = 100;
  title = '';
  editorHasContent = false;
  /** Editor HTML mirrored on every edit — the link preview binds to this, since
   *  a contenteditable's innerHTML isn't something a template can watch. */
  editorHtml = '';

  // ── Toolbar state ─────────────────────────────────────────────────────────
  isBold       = false;
  isItalic     = false;
  isUnderline  = false;
  isStrike     = false;

  zoomLevel         = 100;
  selectedFont      = 'Arial';
  selectedSize      = '00';
  selectedParagraph = 'Paragraph text';
  currentColor      = '#000000';
  currentBgColor    = '#ffffff';

  showParagraphDD = false;
  showFontDD      = false;
  showSizeDD      = false;
  ddPos           = { top: 0, left: 0 };
  showLinkModal   = false;
  showTableModal  = false;
  showTitleEmojiPicker = false;
  showDescEmojiPicker  = false;
  emojiDDPos = { top: 0, left: 0 };

  linkUrl   = '';
  tableRows = 3;
  tableCols = 3;
  savedRange: Range | null = null;
  private _ddSavedRange: Range | null = null;

  // ── API state ─────────────────────────────────────────────────────────────
  isSaving     = false;
  isPublishing = false;
  saveError    = '';
  draftWarning = '';   // shown when user saves draft with tags/collabs selected
  editBlogId: string | null = null;   // set when editing an existing blog
  editingBlogStatus: 'DRAFT' | 'PUBLISHED' | null = null;  // original status of blog being edited

  /**
   * Whether the consent checkbox must be shown and checked before publishing.
   * True for brand-new blogs, AND for edits of a blog that is still a DRAFT
   * (it has never actually been published, so consent has never been
   * certified for it). False only once a blog has already gone through
   * PUBLISHED at least once — consent was captured then.
   *
   * NOTE: while editBlogId is set but the blog hasn't finished loading yet
   * (editingBlogStatus still null), this defaults to true so the checkbox
   * doesn't briefly disappear/reappear or let a publish slip through before
   * we know the blog's real status.
   */
  get requiresConsent(): boolean {
    return !this.editBlogId || this.editingBlogStatus !== 'PUBLISHED';
  }

  // ── Tag people ────────────────────────────────────────────────────────────
  showBlogTagPeople = false;
  blogTagSearchInput = '';
  blogTagSearchResults: BlogUser[] = [];
  blogTaggedPeople: BlogUser[] = [];
  blogTagInputFocused = false;
  private blogTagSearch$ = new Subject<string>();

  // ── Collaborators ─────────────────────────────────────────────────────────
  showBlogCollaborators = false;
  blogCollabSearchInput = '';
  blogCollabSearchResults: BlogUser[] = [];
  blogCollaborators: BlogUser[] = [];
  blogCollabInputFocused = false;
  private blogCollabSearch$ = new Subject<string>();

  // ── Hashtags ──────────────────────────────────────────────────────────────
  blogHashtags: string[] = [];
  blogHashtagInput = '';
  blogHashSuggestions: HashtagSuggestion[] = [];
  blogHashDropOpen = false;
  @ViewChild('blogHashDir') blogHashDir?: HashtagInputDirective;

  // ── Comments ──────────────────────────────────────────────────────────────
  blogCommentsDisabled = false;

  // ── Consent ───────────────────────────────────────────────────────────────
  blogConsentChecked = false;

  // ── Collaborator status (edit mode only) ──────────────────────────────────
  blogCollabStatus: Map<string, 'ACCEPTED' | 'PENDING' | 'DECLINED'> = new Map();

  // ── Media tracking ────────────────────────────────────────────────────────
  /**
   * Registry of existing server media, keyed by their URL (src).
   * Populated during loadBlogForEdit so buildBlocks can match by src instead of
   * relying on data-* attributes that browsers strip inside contenteditable.
   */
  private existingMediaRegistry = new Map<string, { mediaId: string; type: 'IMAGE' | 'VIDEO' }>();

  /**
   * Registry of NEW media added this session, keyed by a stable UUID written as
   * data-media-id on the element at insert time.
   */
  private mediaRegistry = new Map<string, { file: File; type: 'IMAGE' | 'VIDEO'; blobUrl?: string }>();

  /** Generates a short unique id for media element tracking. */
  private newMediaId(): string {
    return 'mid-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
  }

  /** Hover "×" button injected into every inserted image/video, top-right. */
  private readonly removeMediaButtonHtml =
    '<button type="button" class="blog-media-remove" data-remove-media contenteditable="false" title="Remove">' +
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
    'stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
    '</button>';

  /** Click delegation for the editor body — currently just handles the
   *  per-media remove ("×") button, since contenteditable innerHTML can't
   *  carry Angular event bindings. */
  onEditorClick(event: MouseEvent): void {
    const removeBtn = (event.target as HTMLElement).closest('[data-remove-media]');
    if (!removeBtn) return;
    event.preventDefault();
    event.stopPropagation();
    const wrapper = removeBtn.closest('.blog-media-wrap');
    wrapper?.remove();
    this.updateState();
  }

  // ── Lookup tables ─────────────────────────────────────────────────────────
  fontFamilies = [
    'Arial', 'Georgia', 'Times New Roman', 'Courier New',
    'Verdana', 'Trebuchet MS', 'Impact', 'Palatino',
  ];
  fontSizes = [
    '8','9','10','11','12','14','16','18','20','24','28','32','36','48','72',
  ];
  paragraphStyles = [
    { label: 'Paragraph text', tag: 'p'   },
    { label: 'Heading 1',      tag: 'h1'  },
    { label: 'Heading 2',      tag: 'h2'  },
    { label: 'Heading 3',      tag: 'h3'  },
    { label: 'Heading 4',      tag: 'h4'  },
    { label: 'Heading 5',      tag: 'h5'  },
    { label: 'Heading 6',      tag: 'h6'  },
    { label: 'Preformatted',   tag: 'pre' },
  ];

  readonly fontSizeMap: Record<string, string> = {
    '1':'8','2':'10','3':'12','4':'14','5':'18','6':'24','7':'36',
  };
  readonly fontSizeExecMap: Record<string, string> = {
    '8':'1','9':'1','10':'2','11':'2','12':'3','13':'3','14':'4',
    '15':'4','16':'4','17':'4','18':'5','20':'5','24':'6','28':'6',
    '32':'6','36':'7','48':'7','72':'7',
  };

  private readonly MENTION_DEBOUNCE = 200;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly cdr:             ChangeDetectorRef,
    private readonly blogService:     BlogService,
    private readonly userService:     UserService,
    private readonly hashtagService:  HashtagService,
    private readonly router:          Router,
    private readonly route:           ActivatedRoute,
    private readonly location:        Location,
    private readonly toastr:          ToastrService,
  ) {
    // Wire debounced tag search
    this.blogTagSearch$
      .pipe(debounceTime(this.MENTION_DEBOUNCE), distinctUntilChanged())
      .subscribe(q => this.fetchBlogTagUsers(q));

    // Wire debounced collab search
    this.blogCollabSearch$
      .pipe(debounceTime(this.MENTION_DEBOUNCE), distinctUntilChanged())
      .subscribe(q => this.fetchBlogCollabUsers(q));
  }

  /** Goes back to whatever screen the user actually came from (header "Create
   *  blog" link, blog listing edit action, blog details edit action, etc.) —
   *  those entry points don't all carry a returnUrl, so real browser history
   *  is the only thing that reliably knows what preceded this page. Falls
   *  back to the blogs listing only when there's no in-app history to return
   *  to (e.g. the page was opened directly via a shared URL). */
  navigateBack(): void {
    if (isPlatformBrowser(this.platformId) && window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/blogs']);
    }
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Check for edit mode via ?edit=blogId query param
    this.route.queryParams.subscribe(params => {
      const editId = params['edit'];
      if (editId) {
        this.editBlogId = editId;
        this.loadBlogForEdit(editId);
        return;
      }

      // Normal create mode — restore draft, or start from a clean empty
      // paragraph (matches the fallback loadBlogForEdit always uses).
      // A totally empty contenteditable has no block-level element for the
      // caret to live in, so the first execCommand('insertHTML', …) for an
      // inserted image/video can get normalized/nested by the browser in a
      // way that swallows whatever text is typed after it. Seeding a
      // <p><br></p> up front gives every keystroke a real block ancestor
      // from the start, same as the editor already has in edit mode.
      const saved = localStorage.getItem('blog_draft');
      let restoredContent = '';
      if (saved) {
        try {
          const d = JSON.parse(saved);
          if (d.title) this.title = d.title;
          if (d.content) restoredContent = d.content;
        } catch { /* ignore corrupt draft */ }
      }

      setTimeout(() => {
        if (this.editorBody?.nativeElement) {
          this.editorBody.nativeElement.innerHTML = restoredContent || '<p><br></p>';
          this.editorHasContent = (this.editorBody.nativeElement.innerText?.trim() ?? '').length > 0;
          this.editorHtml = this.editorBody.nativeElement.innerHTML;
          this.cdr.markForCheck();
        }
      }, 0);
    });
  }

  private loadBlogForEdit(blogId: string): void {
    this.blogService.getBlog(blogId).subscribe({
      next: (res: any) => {
        const b = res.data;

        // ── Title ─────────────────────────────────────────────────────────
        this.title = b.title ?? '';

        // ── Track original status ──────────────────────────────────────────
        this.editingBlogStatus = (b.blogInfo?.status ?? 'PUBLISHED') as 'DRAFT' | 'PUBLISHED';

        // ── Reconstruct editor HTML from ALL blocks in position order ──────
        // KEY INSIGHT: browsers strip unknown data-* attributes inside contenteditable,
        // so we CANNOT rely on data-existing-media-id surviving in the DOM.
        // Instead we register each server media by its src URL — browsers always preserve src.
        this.existingMediaRegistry.clear();

        const blocks: any[] = (b.blogInfo?.blocks ?? [])
          .slice()
          .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));

        let editorHtml = '';
        for (const blk of blocks) {
          if (blk.type === 'TEXT') {
            editorHtml += blk.text ?? '';

          } else if (blk.type === 'IMAGE' && blk.mediaId) {
            const src = blk.mediaId.urlLarge || blk.mediaId.urlMedium || blk.mediaId.url || '';
            const existingId = blk.mediaId._id ?? '';
            if (src && existingId) {
              this.existingMediaRegistry.set(src, { mediaId: existingId, type: 'IMAGE' });
              editorHtml +=
                `<span class="blog-media-wrap" contenteditable="false" style="position:relative;display:block;margin:1rem 0;">` +
                `<img src="${src}"` +
                ` style="max-width:100%;height:auto;border-radius:6px;display:block;margin:0;" />` +
                this.removeMediaButtonHtml +
                `</span><p><br></p>`;
            }

          } else if (blk.type === 'VIDEO' && blk.mediaId) {
            const src = blk.mediaId.urlLarge || blk.mediaId.urlMedium || blk.mediaId.url || '';
            const existingId = blk.mediaId._id ?? '';
            if (src && existingId) {
              this.existingMediaRegistry.set(src, { mediaId: existingId, type: 'VIDEO' });
              editorHtml +=
                `<div class="blog-media-wrap" contenteditable="false" style="position:relative;margin:0.5rem 0;">` +
                `<video controls src="${src}"` +
                ` style="max-width:100%;border-radius:6px;display:block;width:100%;"></video>` +
                this.removeMediaButtonHtml +
                `</div><p><br></p>`;
            }
          }
        }

        // Inject into editor after the view has rendered
        setTimeout(() => {
          if (this.editorBody?.nativeElement) {
            this.editorBody.nativeElement.innerHTML = editorHtml || '<p><br></p>';
            this.editorHasContent = (this.editorBody.nativeElement.innerText?.trim() ?? '').length > 0;
            this.editorHtml = this.editorBody.nativeElement.innerHTML;
            this.cdr.markForCheck();
          }
        }, 0);

        // ── Hashtags ──────────────────────────────────────────────────────
        const rawHashtags: string[] = b.hashtags ?? [];
        this.blogHashtags = rawHashtags
          .map((h: string) => h.replace(/^#/, '').trim())
          .filter(Boolean);

        // ── Comments disabled ─────────────────────────────────────────────
        this.blogCommentsDisabled = b.blogInfo?.commentsDisabled ?? false;

        // ── Tagged people (mentions) ──────────────────────────────────────
        const mentions: any[] = b.blogInfo?.mentions ?? [];
        this.blogTaggedPeople = mentions
          .filter((u: any) => u && (u._id || u.id))
          .map((u: any) => ({
            _id: u._id ?? u.id ?? '',
            displayName: u.displayName ?? '',
            userName: u.userName ?? '',
            profileImage: u.profileImage ?? null,
            isFriend: false,
          }));

        // ── Collaborators (with status) ────────────────────────────────────
        const collabs: any[] = b.blogInfo?.collaborations ?? [];
        this.blogCollabStatus = new Map();
        this.blogCollaborators = collabs
          .filter((c: any) => c.user && typeof c.user === 'object' && (c.user._id || c.user.id))
          .map((c: any) => {
            const status = (c.status ?? 'PENDING') as 'ACCEPTED' | 'PENDING' | 'DECLINED';
            const userId = c.user._id ?? c.user.id ?? '';
            this.blogCollabStatus.set(userId, status);
            return {
              _id: userId,
              displayName: c.user.displayName ?? '',
              userName: c.user.userName ?? '',
              profileImage: c.user.profileImage ?? null,
              isFriend: false,
            };
          });

        // Auto-expand sections that have data
        if (this.blogCollaborators.length > 0) this.showBlogCollaborators = true;
        if (this.blogTaggedPeople.length > 0) this.showBlogTagPeople = true;

        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to load blog for edit:', err);
      },
    });
  }

  // ── Click-outside closes dropdowns ───────────────────────────────────────
  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target.closest('.dd-host')) {
      this.closeAllDD();
    }
    // Close the tag/collaborator search-results panels deterministically on the
    // first outside click, instead of relying solely on the (blur) handler's
    // 150ms setTimeout — that delay could require a second click before this
    // component's next change-detection pass picked up the flag flip.
    if (this.blogTagInputFocused && !target.closest('[data-blog-tag-section]')) {
      this.blogTagInputFocused = false;
    }
    if (this.blogCollabInputFocused && !target.closest('[data-blog-collab-section]')) {
      this.blogCollabInputFocused = false;
    }
  }

  // Dropdowns are position:fixed (to escape the toolbar's scroll clipping),
  // so their computed coordinates go stale if the page scrolls — close them then.
  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.closeAllDD();
  }

  closeAllDD(): void {
    this.showParagraphDD = false;
    this.showFontDD      = false;
    this.showSizeDD      = false;
    this.showTitleEmojiPicker = false;
    this.showDescEmojiPicker  = false;
  }

  // ── Emoji pickers (title + description) ─────────────────────────────────

  toggleTitleEmojiPicker(): void {
    const willOpen = !this.showTitleEmojiPicker;
    this.closeAllDD();
    this.showTitleEmojiPicker = willOpen;
  }

  pickTitleEmoji(emoji: string): void {
    this.title = (this.title + emoji).slice(0, this.MAX_TITLE_LENGTH);
    this.showTitleEmojiPicker = false;
  }

  toggleDescEmojiPicker(btn?: HTMLElement, toolbarRow?: HTMLElement): void {
    const willOpen = !this.showDescEmojiPicker;
    const sel = window.getSelection();
    if (sel?.rangeCount) this._ddSavedRange = sel.getRangeAt(0).cloneRange();
    this.closeAllDD();
    this.showDescEmojiPicker = willOpen;
    if (willOpen && btn) {
      const btnRect = btn.getBoundingClientRect();
      const bottom  = toolbarRow ? toolbarRow.getBoundingClientRect().bottom : btnRect.bottom;
      this.emojiDDPos = { top: bottom + 4, left: btnRect.left };
    }
  }

  pickDescEmoji(emoji: string): void {
    this.restoreDDRange();
    document.execCommand('insertText', false, emoji);
    this.updateState();
    this.showDescEmojiPicker = false;
  }

  // ── Editor helpers ────────────────────────────────────────────────────────

  focus(): void { this.editorBody?.nativeElement?.focus(); }

  exec(cmd: string, val?: string): void {
    this.focus();
    document.execCommand(cmd, false, val);
    this.updateState();
  }

  updateState(): void {
    this.isBold      = document.queryCommandState('bold');
    this.isItalic    = document.queryCommandState('italic');
    this.isUnderline = document.queryCommandState('underline');
    this.isStrike    = document.queryCommandState('strikethrough');
    const fn = document.queryCommandValue('fontName');
    if (fn) this.selectedFont = fn.replace(/['"]/g, '');
    const fs = document.queryCommandValue('fontSize');
    if (fs && this.fontSizeMap[fs]) this.selectedSize = this.fontSizeMap[fs];
    this.editorHasContent = (this.editorBody?.nativeElement?.innerText?.trim() ?? '').length > 0;
    // Safety net for any edit path that reaches keyup/mouseup but not `input`.
    this.editorHtml = this.editorBody?.nativeElement?.innerHTML ?? '';
    this.cdr.markForCheck();
  }

  /**
   * Mirrors the editor's HTML so the link preview can react to it, and refreshes
   * `editorHasContent` (a paste fires neither `keyup` nor `mouseup`, so without
   * this the "Start typing here…" placeholder stayed on top of pasted content).
   *
   * Deliberately kept separate from `updateState()` so editor/toolbar behaviour
   * is unchanged, and wired to `input`, `paste` and the existing keyup/mouseup
   * paths — browsers differ on which of those a paste produces, and the preview
   * only needs whichever arrives first.
   */
  onEditorInput(): void {
    const el = this.editorBody?.nativeElement;
    this.editorHtml = el?.innerHTML ?? '';
    this.editorHasContent = (el?.innerText?.trim() ?? '').length > 0;
    this.cdr.markForCheck();
  }

  /**
   * `paste` fires *before* the clipboard content lands in the DOM, so re-read on
   * the next tick. Does not touch the paste itself — formatting is preserved
   * exactly as before.
   */
  onEditorPaste(): void {
    setTimeout(() => this.onEditorInput(), 0);
  }

  toggleDD(name: 'paragraph' | 'font' | 'size', btn?: HTMLElement, toolbarRow?: HTMLElement): void {
    const sel = window.getSelection();
    if (sel?.rangeCount) this._ddSavedRange = sel.getRangeAt(0).cloneRange();

    const willOpen =
      name === 'paragraph' ? !this.showParagraphDD :
      name === 'font'      ? !this.showFontDD      :
                              !this.showSizeDD;

    this.showParagraphDD = name === 'paragraph' ? willOpen : false;
    this.showFontDD      = name === 'font'      ? willOpen : false;
    this.showSizeDD      = name === 'size'      ? willOpen : false;

    if (willOpen && btn) {
      // Anchor to the bottom of the whole toolbar row (not just the clicked
      // button) — the toolbar wraps onto a second line on wider screens, so
      // anchoring to the button alone would land the menu on top of that row.
      const btnRect = btn.getBoundingClientRect();
      const bottom  = toolbarRow ? toolbarRow.getBoundingClientRect().bottom : btnRect.bottom;
      this.ddPos = { top: bottom + 4, left: btnRect.left };
    }
  }

  private restoreDDRange(): void {
    this.editorBody?.nativeElement?.focus();
    if (this._ddSavedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(this._ddSavedRange);
      this._ddSavedRange = null;
    }
  }

  setParagraph(style: { label: string; tag: string }): void {
    this.selectedParagraph = style.label;
    this.restoreDDRange();
    document.execCommand('formatBlock', false, `<${style.tag}>`);
    this.updateState();
    this.showParagraphDD = false;
  }

  setFont(font: string): void {
    this.selectedFont = font;
    this.restoreDDRange();
    document.execCommand('fontName', false, font);
    this.updateState();
    this.showFontDD = false;
  }

  setSize(size: string): void {
    this.selectedSize = size;
    this.restoreDDRange();
    document.execCommand('fontSize', false, this.fontSizeExecMap[size] || '3');
    this.updateState();
    this.showSizeDD = false;
  }

  incSize(): void {
    const idx = this.fontSizes.indexOf(this.selectedSize);
    if (idx < this.fontSizes.length - 1) this.setSize(this.fontSizes[idx + 1]);
  }

  decSize(): void {
    const idx = this.fontSizes.indexOf(this.selectedSize);
    if (idx > 0) this.setSize(this.fontSizes[idx - 1]);
  }

  setColor(val: string):   void { this.currentColor   = val; this.exec('foreColor',  val); }
  setBgColor(val: string): void { this.currentBgColor = val; this.exec('hiliteColor', val); }

  zoom(delta: number): void {
    this.zoomLevel = Math.min(200, Math.max(50, this.zoomLevel + delta));
  }

  // ── Link ──────────────────────────────────────────────────────────────────

  openLink(): void {
    const sel = window.getSelection();
    if (sel?.rangeCount) this.savedRange = sel.getRangeAt(0).cloneRange();
    this.showLinkModal = true;
  }

  insertLink(): void {
    if (this.savedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(this.savedRange);
    }
    const url = this.linkUrl.startsWith('http')
      ? this.linkUrl
      : 'https://' + this.linkUrl;
    this.exec('createLink', url);
    this.showLinkModal = false;
    this.linkUrl = '';
    this.savedRange = null;
  }

  // ── Media insert ──────────────────────────────────────────────────────────

  insertImage(): void { this.imageInput.nativeElement.click(); }
  insertPhoto(): void { this.photoInput.nativeElement.click(); }
  insertVideo(): void { this.videoInput.nativeElement.click(); }

  onImageFile(e: Event): void {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    // Generate a UUID that will be written onto the DOM element as data-media-id.
    // This is the ONLY key used for registry lookups — never the src/blob URL,
    // because browsers rewrite src attributes inside contenteditable.
    const mediaId = this.newMediaId();

    if (isVideo) {
      const blobUrl = URL.createObjectURL(file);

      // Register by UUID BEFORE inserting into DOM
      this.mediaRegistry.set(mediaId, { file, type: 'VIDEO', blobUrl });

      // Write data-media-id onto the element — this attribute is preserved
      // by contenteditable because it starts with "data-" and is on a known element.
      // Also add data-media-id to a wrapper <div> so querySelectorAll can find it
      // even if the browser wraps the video in a block container.
      this.exec(
        'insertHTML',
        `<div class="blog-media-wrap" contenteditable="false" data-media-id="${mediaId}" data-media-type="VIDEO" ` +
        `style="position:relative;margin:0.5rem 0;">` +
        `<video controls src="${blobUrl}" ` +
        `style="max-width:100%;border-radius:6px;display:block;width:100%;"></video>` +
        this.removeMediaButtonHtml +
        `</div><p><br></p>`,
      );
      this.moveCaretAfterMedia(mediaId);
    } else {
      // Image — use FileReader for a stable base64 data URL
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;

        // Register by UUID BEFORE inserting
        this.mediaRegistry.set(mediaId, { file, type: 'IMAGE' });

        // Insert a wrapper <span> with data-media-id so buildBlocks() can
        // find it reliably regardless of how the browser normalizes the img tag.
        this.exec(
          'insertHTML',
          `<span class="blog-media-wrap" contenteditable="false" style="position:relative;display:block;margin:1rem 0;">` +
          `<img src="${dataUrl}" data-media-id="${mediaId}" ` +
          `style="max-width:100%;height:auto;border-radius:6px;display:block;margin:0;" />` +
          this.removeMediaButtonHtml +
          `</span><p><br></p>`,
        );
        this.moveCaretAfterMedia(mediaId);
      };
      // HEIC: previewed via a temporary JPEG; the original HEIC is uploaded
      // and the API converts it to WebP.
      reader.onerror = () => {
        if (isHeicFile(file)) this.toastr.error(`Couldn't read "${file.name}". Please try another image.`);
      };
      readAsPreviewDataUrl(reader, file);
    }

    // Reset so the same file can be re-selected
    (e.target as HTMLInputElement).value = '';
  }

  /**
   * After inserting a media block, the browser does NOT reliably leave the
   * caret in the trailing placeholder <p><br></p> — for a block-level
   * contenteditable="false" element (the video wrapper <div>) it instead
   * collapses the selection to BEFORE that element. Left alone, whatever the
   * user types next merges into the text that came before the media instead
   * of starting a new paragraph after it, so it silently disappears from
   * "below the media" where the user actually typed it. Explicitly moving
   * the caret into the placeholder paragraph fixes this for both wrapper
   * types.
   */
  private moveCaretAfterMedia(mediaId: string): void {
    const root = this.editorBody?.nativeElement;
    if (!root) return;
    // data-media-id sits on the outer .blog-media-wrap for video, but on the
    // inner <img> (not the wrapping <span>) for images — normalize to the
    // wrapper either way so we land on its actual next sibling.
    const hit = root.querySelector(`[data-media-id="${mediaId}"]`);
    const wrap = hit?.closest('.blog-media-wrap') ?? hit;
    const trailingP = wrap?.nextElementSibling;
    if (!trailingP) return;
    const range = document.createRange();
    range.selectNodeContents(trailingP);
    range.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  // ── Table / Code / Quote ──────────────────────────────────────────────────

  insertTable(): void {
    let html = '<table style="border-collapse:collapse;width:100%;margin-bottom:1rem"><tbody>';
    for (let r = 0; r < this.tableRows; r++) {
      html += '<tr>';
      for (let c = 0; c < this.tableCols; c++) {
        html += '<td style="border:1px solid #d1d5db;padding:8px 12px;min-width:60px">&nbsp;</td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table><p><br></p>';
    this.exec('insertHTML', html);
    this.showTableModal = false;
  }

  insertCode(): void {
    this.exec(
      'insertHTML',
      '<pre style="background:#f4f4f4;padding:12px 16px;border-radius:6px;' +
      'font-family:monospace;font-size:.875rem;margin:1rem 0;border:1px solid #e5e7eb">' +
      '<code>// Your code here</code></pre><p><br></p>',
    );
  }

  insertQuote(): void {
    this.exec(
      'insertHTML',
      '<blockquote style="border-left:4px solid #7c3aed;padding:8px 16px;margin:1rem 0;' +
      'color:#555;font-style:italic;background:#faf5ff">Quote text here</blockquote><p><br></p>',
    );
  }

  // ── Block builder ─────────────────────────────────────────────────────────

  /**
   * Walks the ENTIRE editor DOM (not just top-level children) to find
   * every <img> and <video> element in document order.
   *
   * Strategy:
   *   1. Use TreeWalker to collect all nodes in document order.
   *   2. For each node decide if it is a media element (IMG/VIDEO) or text content.
   *   3. Text between/around media elements is captured as the outerHTML of the
   *      nearest block-level ancestor so formatting is preserved.
   *   4. Consecutive text ancestors are merged into one TEXT block.
   *
   * Lookup key priority for media elements:
   *   - data-media-key attribute  (set explicitly for <video> elements)
   *   - src attribute             (works for <img> base64 URLs)
   */
  private buildBlocks(): IBlockInput[] {
    const root = this.editorBody?.nativeElement;
    if (!root) return [];

    const blocks: IBlockInput[] = [];
    let position = 0;

    // ── Walk every <img> and <video> in the editor DOM ────────────────────────
    // For each one, check existingMediaRegistry (server URLs) first, then
    // mediaRegistry (new files added this session, keyed by data-media-id UUID).
    //
    // Strategy: iterate top-level children in order. For each child decide:
    //   • img/video at root      → media node
    //   • wrapper containing img/video → split it
    //   • pure text element      → text block

    type Segment =
      | { kind: 'existing'; existingMediaId: string; mediaType: 'IMAGE' | 'VIDEO' }
      | { kind: 'media';    mediaId: string }
      | { kind: 'block';    el: HTMLElement };

    const segments: Segment[] = [];

    const classifyEl = (el: HTMLElement): Segment | null => {
      // ── Existing server image ─────────────────────────────────────────
      if (el.tagName === 'IMG') {
        const src = el.getAttribute('src') ?? '';
        const reg = this.existingMediaRegistry.get(src);
        if (reg) return { kind: 'existing', existingMediaId: reg.mediaId, mediaType: reg.type };
        // New image (base64 / blob from FileReader) — look up by data-media-id
        const mid = el.getAttribute('data-media-id') ?? '';
        if (mid) return { kind: 'media', mediaId: mid };
        return null; // untracked image — skip
      }

      // ── Existing server video ─────────────────────────────────────────
      if (el.tagName === 'VIDEO') {
        const src = el.getAttribute('src') ?? '';
        const reg = this.existingMediaRegistry.get(src);
        if (reg) return { kind: 'existing', existingMediaId: reg.mediaId, mediaType: reg.type };
        return null;
      }

      // ── Wrapper div/p — look for img or video inside ──────────────────
      const img = el.querySelector('img');
      if (img) {
        const src = img.getAttribute('src') ?? '';
        const reg = this.existingMediaRegistry.get(src);
        if (reg) return { kind: 'existing', existingMediaId: reg.mediaId, mediaType: reg.type };
        const mid = img.getAttribute('data-media-id') ?? '';
        if (mid) return { kind: 'media', mediaId: mid };
      }

      const video = el.querySelector('video');
      if (video) {
        const src = video.getAttribute('src') ?? '';
        const reg = this.existingMediaRegistry.get(src);
        if (reg) return { kind: 'existing', existingMediaId: reg.mediaId, mediaType: reg.type };
        // New video — the wrapper div has data-media-id
        const mid = el.getAttribute('data-media-id') ?? '';
        if (mid) return { kind: 'media', mediaId: mid };
      }

      return null; // pure text element
    };

    root.childNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        const text = (node.textContent ?? '').trim();
        if (text) {
          const wrapper = document.createElement('p');
          wrapper.textContent = text;
          segments.push({ kind: 'block', el: wrapper });
        }
        return;
      }

      const el = node as HTMLElement;
      const classified = classifyEl(el);

      if (classified) {
        segments.push(classified);
      } else {
        // Text element — check if it contains any media as a descendant (mixed block)
        const hasImg   = !!el.querySelector('img');
        const hasVideo = !!el.querySelector('video');
        if (hasImg || hasVideo) {
          // Split around each media child
          this.splitMixedBlockByURL(el, segments);
        } else {
          segments.push({ kind: 'block', el });
        }
      }
    });

    // ── Convert segments → IBlockInput[] ─────────────────────────────────────

    let textBuffer = '';

    const flushText = () => {
      const clean = textBuffer.replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, '').trim();
      if (clean) {
        blocks.push({ type: 'TEXT', position: position++, text: textBuffer.trim() });
      }
      textBuffer = '';
    };

    for (const seg of segments) {
      if (seg.kind === 'block') {
        textBuffer += seg.el.outerHTML;
      } else if (seg.kind === 'existing') {
        flushText();
        blocks.push({ type: seg.mediaType, position: position++, existingMediaId: seg.existingMediaId });
      } else {
        // kind === 'media' — new file added this session
        flushText();
        const tracked = this.mediaRegistry.get(seg.mediaId);
        if (tracked) {
          blocks.push({ type: tracked.type, position: position++, file: tracked.file });
        }
      }
    }

    flushText();
    return blocks;
  }


  /**
   * Splits a mixed element that contains text alongside img/video children.
   * Uses existingMediaRegistry (URL lookup) instead of data-* attributes.
   */
  private splitMixedBlockByURL(
    el: HTMLElement,
    out: Array<
      | { kind: 'existing'; existingMediaId: string; mediaType: 'IMAGE' | 'VIDEO' }
      | { kind: 'media';    mediaId: string }
      | { kind: 'block';    el: HTMLElement }
    >,
  ): void {
    let textBuffer = '';

    const flushText = () => {
      const trimmed = textBuffer.trim();
      if (trimmed) {
        const wrapper = document.createElement('p');
        wrapper.innerHTML = trimmed;
        out.push({ kind: 'block', el: wrapper });
      }
      textBuffer = '';
    };

    const processNode = (child: ChildNode) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childEl = child as HTMLElement;

        if (childEl.tagName === 'IMG') {
          const src = childEl.getAttribute('src') ?? '';
          const reg = this.existingMediaRegistry.get(src);
          if (reg) {
            flushText();
            out.push({ kind: 'existing', existingMediaId: reg.mediaId, mediaType: reg.type });
            return;
          }
          const mid = childEl.getAttribute('data-media-id') ?? '';
          if (mid) { flushText(); out.push({ kind: 'media', mediaId: mid }); return; }
          textBuffer += childEl.outerHTML;
          return;
        }

        if (childEl.tagName === 'VIDEO') {
          const src = childEl.getAttribute('src') ?? '';
          const reg = this.existingMediaRegistry.get(src);
          if (reg) {
            flushText();
            out.push({ kind: 'existing', existingMediaId: reg.mediaId, mediaType: reg.type });
            return;
          }
          // New video — id lives on the .blog-media-wrap ancestor, not the <video> itself
          const wrapMid = childEl.closest<HTMLElement>('[data-media-id]')?.getAttribute('data-media-id') ?? '';
          if (wrapMid) { flushText(); out.push({ kind: 'media', mediaId: wrapMid }); return; }
          textBuffer += childEl.outerHTML;
          return;
        }

        // A wrapper carrying its own data-media-id (e.g. .blog-media-wrap) is a
        // single media unit — treat it as such instead of recursing into its
        // children, which would strand the id on a parent no longer being read.
        const ownMid = childEl.getAttribute('data-media-id') ?? '';
        if (ownMid) { flushText(); out.push({ kind: 'media', mediaId: ownMid }); return; }

        // Recurse for wrappers
        const hasMedia = childEl.querySelector('img, video');
        if (hasMedia) {
          childEl.childNodes.forEach(processNode);
        } else {
          textBuffer += childEl.outerHTML;
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (text.trim()) textBuffer += text;
      }
    };

    el.childNodes.forEach(processNode);
    flushText();
  }

  /**
   * Unified split: walks a mixed element and emits text, existing-media, and
   * new-media segments in document order.
   */
  private splitMixedBlock(
    el: HTMLElement,
    out: Array<
      | { kind: 'media';    mediaId: string }
      | { kind: 'existing'; existingMediaId: string; mediaType: 'IMAGE' | 'VIDEO' }
      | { kind: 'block';    el: HTMLElement }
    >,
  ): void {
    let textBuffer = '';

    const flushText = () => {
      const trimmed = textBuffer.trim();
      if (trimmed) {
        const wrapper = document.createElement('p');
        wrapper.innerHTML = trimmed;
        out.push({ kind: 'block', el: wrapper });
      }
      textBuffer = '';
    };

    const processNode = (child: ChildNode) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childEl = child as HTMLElement;

        // Existing server media
        if (childEl.getAttribute('data-existing-media') === 'true') {
          flushText();
          const existingId = childEl.getAttribute('data-existing-media-id') ?? '';
          if (existingId) {
            const mediaType: 'IMAGE' | 'VIDEO' =
              childEl.tagName === 'IMG' || childEl.querySelector('img')
                ? 'IMAGE'
                : 'VIDEO';
            out.push({ kind: 'existing', existingMediaId: existingId, mediaType });
          }
          return;
        }

        // New media (this session)
        const mediaId = childEl.getAttribute('data-media-id');
        if (mediaId) {
          flushText();
          out.push({ kind: 'media', mediaId });
          return;
        }

        // Recurse into wrapper elements that might contain media
        const hasMedia =
          childEl.querySelector('[data-existing-media]') ||
          childEl.querySelector('[data-media-id]');
        if (hasMedia) {
          childEl.childNodes.forEach(processNode);
        } else {
          textBuffer += childEl.outerHTML;
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (text.trim()) textBuffer += text;
      }
    };

    el.childNodes.forEach(processNode);
    flushText();
  }

  /**
   * Splits a mixed block (containing both text and [data-media-id] elements)
   * into ordered segments. Also handles [data-existing-media] if encountered.
   */
  private splitMixedBlockByMediaId(
    el: HTMLElement,
    out: Array<
      | { kind: 'media';    mediaId: string }
      | { kind: 'existing'; existingMediaId: string; mediaType: 'IMAGE' | 'VIDEO' }
      | { kind: 'block';    el: HTMLElement }
    >,
  ): void {
    // Delegate to the unified helper
    this.splitMixedBlock(el, out);
  }

  // ── Save / Publish ────────────────────────────────────────────────────────

  saveDraft(): void {
    if (this.isSaving) return;
    // Only auto-save to localStorage in create mode, not when editing an existing blog
    if (!this.editBlogId) {
      this.autoSaveToLocalStorage();
    }

    // Warn user that tags and collaborators are not saved with drafts
    const hasTags   = this.blogTaggedPeople.length > 0;
    const hasCollabs = this.blogCollaborators.length > 0;
    if (hasTags || hasCollabs) {
      const parts: string[] = [];
      if (hasTags)   parts.push(`${this.blogTaggedPeople.length} tagged ${this.blogTaggedPeople.length === 1 ? 'person' : 'people'}`);
      if (hasCollabs) parts.push(`${this.blogCollaborators.length} ${this.blogCollaborators.length === 1 ? 'collaborator' : 'collaborators'}`);
      this.draftWarning = `Draft saved without ${parts.join(' and ')}. Tags and collaborators are only applied when you publish.`;
      // Clear them so backend doesn't receive them
      this.blogTaggedPeople  = [];
      this.blogCollaborators = [];
    } else {
      this.draftWarning = '';
    }

    this.saveToBackend('DRAFT');
  }

  publish(): void {
    if (this.isPublishing) return;
    if (this.requiresConsent && !this.blogConsentChecked) {
      this.saveError = 'You must certify consent before publishing.';
      return;
    }
    this.draftWarning = '';
    this.saveToBackend('PUBLISHED');
  }

  private saveToBackend(status: BlogStatus): void {
    if (!this.title.trim()) {
      this.saveError = 'Please enter a blog title before publishing.';
      return;
    }

    if (this.title.trim().length > this.MAX_TITLE_LENGTH) {
      this.saveError = `Blog title cannot exceed ${this.MAX_TITLE_LENGTH} characters.`;
      return;
    }

    this.saveError  = '';
    const blocks    = this.buildBlocks();
    const isDraft   = status === 'DRAFT';

    isDraft ? (this.isSaving = true) : (this.isPublishing = true);

    const payload = {
      title:    this.title.trim(),
      blocks,
      hashtags: this.buildHashtagsString(),
      audience: 'EVERYONE' as const,
      status,
      // Always send the current arrays (even when empty) rather than
      // collapsing an empty selection to `undefined`. On edit, an empty
      // array is the only way to tell the backend "remove everyone" — if we
      // send `undefined` instead, the backend treats the field as untouched
      // and the last tagged person / collaborator never gets removed.
      mentions:        this.blogTaggedPeople.map(u => u._id),
      collaboratorIds: this.blogCollaborators.map(u => u._id),
      commentsDisabled: this.blogCommentsDisabled || undefined,
    };

    const request$ = this.editBlogId
      ? this.blogService.updateBlog(this.editBlogId, payload)
      : this.blogService.createBlog(payload);

    request$.subscribe({
      next: (res: any) => {
        isDraft ? (this.isSaving = false) : (this.isPublishing = false);

        if (isPlatformBrowser(this.platformId)) {
          localStorage.removeItem('blog_draft');
        }

        // Revoke all blob URLs to free memory
        this.mediaRegistry.forEach((entry) => {
          if (entry.type === 'VIDEO' && entry.blobUrl) {
            URL.revokeObjectURL(entry.blobUrl);
          }
        });
        this.mediaRegistry.clear();
        this.existingMediaRegistry.clear();

        // Reset tag / collab / hashtag
        this.blogTaggedPeople   = [];
        this.blogCollaborators  = [];
        this.blogHashtags       = [];
        this.blogHashtagInput   = '';
        this.blogHashSuggestions = [];
        this.blogHashDropOpen    = false;
        this.blogTagSearchInput = '';
        this.blogCollabSearchInput = '';
        this.blogCommentsDisabled = false;
        this.blogConsentChecked = false;
        this.editBlogId = null;
        this.editingBlogStatus = null;

        this.blogService.triggerRefresh();

        // After saving draft in edit mode → go back to blog detail so user sees the draft
        // After publishing → also go to blog detail
        const blogId = res?.data?._id;
        if (blogId) {
          this.router.navigate(['/blog', blogId]);
        } else {
          this.router.navigate(['/blogs']);
        }
      },
      error: (err: any) => {
        isDraft ? (this.isSaving = false) : (this.isPublishing = false);
        this.saveError =
          err?.error?.message ?? 'Something went wrong. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private autoSaveToLocalStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const data = {
      title:   this.title,
      content: this.editorBody?.nativeElement?.innerHTML ?? '',
    };
    localStorage.setItem('blog_draft', JSON.stringify(data));
  }

  private extractHashtags(): string {
    const text = this.editorBody?.nativeElement?.innerText ?? '';
    const matches = text.match(/#[\w]+/g);
    return matches ? [...new Set(matches)].join(' ') : '';
  }

  // ── Hashtag methods ───────────────────────────────────────────────────────

  addBlogHashtag(event: Event): void {
    event.preventDefault();
    const raw = this.blogHashtagInput.trim().replace(/^#+/, '').replace(/[,\s]+$/, '').trim();
    if (!raw || this.blogHashtags.includes(raw)) { this.blogHashtagInput = ''; return; }
    this.blogHashtags.push(raw);
    this.blogHashtagInput = '';
  }

  onBlogHashSearch(partial: string): void {
    this.hashtagService.searchHashtags(partial).subscribe(results => {
      this.blogHashSuggestions = results;
      this.cdr.detectChanges();
    });
  }

  onBlogHashSelected(tag: HashtagSuggestion): void {
    const clean = tag.tag.replace(/^#+/, '');
    if (!this.blogHashtags.includes(clean)) this.blogHashtags.push(clean);
    if (this.blogHashDir) { this.blogHashDir.setText(''); this.blogHashDir.closeDropdown(); }
    this.blogHashtagInput    = '';
    this.blogHashDropOpen    = false;
    this.blogHashSuggestions = [];
  }

  removeBlogHashtag(tag: string): void {
    this.blogHashtags = this.blogHashtags.filter(t => t !== tag);
  }

  private buildHashtagsString(): string {
    // Combine manual hashtags + auto-extracted from editor content
    const manual    = this.blogHashtags.map(t => `#${t}`);
    const extracted = this.extractHashtags().split(' ').filter(Boolean);
    const merged    = [...new Set([...manual, ...extracted])];
    return merged.join(' ');
  }

  // ── Tag people methods ────────────────────────────────────────────────────

  toggleBlogTagPeople(): void {
    this.showBlogTagPeople = !this.showBlogTagPeople;
    if (this.showBlogTagPeople) {
      this.blogTagSearchInput = '';
      this.blogTagSearchResults = [];
      this.fetchBlogTagUsers('');
    }
  }

  onBlogTagSearchInput(query: string): void {
    this.blogTagSearch$.next(query);
  }

  onBlogTagInputBlur(): void {
    // Delay so a (mousedown) selection on a result button — which fires before
    // blur — has already run addBlogTag() before we hide the list.
    setTimeout(() => { this.blogTagInputFocused = false; }, 150);
  }

  private fetchBlogTagUsers(query: string): void {
    this.userService.searchUsersForMention(query).subscribe({
      next: (res: any) => {
        const all: BlogUser[] = (res?.data ?? []);
        const taggedIds = new Set(this.blogTaggedPeople.map(u => u._id));
        this.blogTagSearchResults = all.filter(u => !taggedIds.has(u._id));
        this.cdr.detectChanges();
      },
      error: () => { this.blogTagSearchResults = []; },
    });
  }

  addBlogTag(user: BlogUser): void {
    if (this.blogTaggedPeople.find(u => u._id === user._id)) return;
    this.blogTaggedPeople.push(user);
    this.blogTagSearchResults = this.blogTagSearchResults.filter(u => u._id !== user._id);
  }

  removeBlogTag(userId: string): void {
    this.blogTaggedPeople = this.blogTaggedPeople.filter(u => u._id !== userId);
  }

  // ── Collaborator methods ──────────────────────────────────────────────────

  toggleBlogCollaborators(): void {
    this.showBlogCollaborators = !this.showBlogCollaborators;
    if (this.showBlogCollaborators) {
      this.blogCollabSearchInput = '';
      this.blogCollabSearchResults = [];
      this.fetchBlogCollabUsers('');
    }
  }

  onBlogCollabSearchInput(query: string): void {
    this.blogCollabSearch$.next(query);
  }

  onBlogCollabInputBlur(): void {
    setTimeout(() => { this.blogCollabInputFocused = false; }, 150);
  }

  private fetchBlogCollabUsers(query: string): void {
    this.userService.searchUsersForCollaboration(query).subscribe({
      next: (res: any) => {
        const all: BlogUser[] = (res?.data ?? []);
        const collabIds = new Set(this.blogCollaborators.map(u => u._id));
        this.blogCollabSearchResults = all.filter(u => !collabIds.has(u._id));
        this.cdr.detectChanges();
      },
      error: () => { this.blogCollabSearchResults = []; },
    });
  }

  addBlogCollaborator(user: BlogUser): void {
    if (this.blogCollaborators.find(u => u._id === user._id)) return;
    this.blogCollaborators.push(user);
    this.blogCollabSearchResults = this.blogCollabSearchResults.filter(u => u._id !== user._id);
  }

  removeBlogCollaborator(userId: string): void {
    this.blogCollaborators = this.blogCollaborators.filter(u => u._id !== userId);
  }
}